import { useEffect, useRef, useCallback } from 'react';

const VERSION_CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds
const VERSION_URL = '/version.json';

/**
 * Hook that checks for new app versions and auto-reloads when detected.
 * - Fetches version on mount
 * - Polls every hour (backup)
 * - Checks on tab focus (primary mechanism)
 * - Auto-reloads when new version detected and tab becomes visible
 */
export function useVersionCheck() {
  const currentVersionRef = useRef(null);
  const newVersionAvailableRef = useRef(false);

  // Fetch the current version from server
  const fetchVersion = useCallback(async () => {
    try {
      // Add cache-busting query param to ensure fresh response
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.version;
    } catch (error) {
      // Silently fail - version check is not critical
      return null;
    }
  }, []);

  // Check for new version
  const checkForUpdate = useCallback(async () => {
    const serverVersion = await fetchVersion();
    if (!serverVersion) return;

    // First time - just store the version
    if (!currentVersionRef.current) {
      currentVersionRef.current = serverVersion;
      return;
    }

    // If version changed, mark as update available
    if (serverVersion !== currentVersionRef.current) {
      newVersionAvailableRef.current = true;
    }
  }, [fetchVersion]);

  // Handle visibility change - reload if new version available when tab becomes visible
  const handleVisibilityChange = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;

    // If we already know there's a new version, reload now
    if (newVersionAvailableRef.current) {
      window.location.reload();
      return;
    }

    // Otherwise, check for updates when tab becomes visible
    const serverVersion = await fetchVersion();
    if (serverVersion && currentVersionRef.current && serverVersion !== currentVersionRef.current) {
      // New version detected - reload immediately since user just returned
      window.location.reload();
    }
  }, [fetchVersion]);

  useEffect(() => {
    // Initial version check
    checkForUpdate();

    // Set up hourly polling (backup for users who never switch tabs)
    const intervalId = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);

    // Listen for visibility changes (primary mechanism)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate, handleVisibilityChange]);
}

export default useVersionCheck;
